import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, CheckCircle } from 'lucide-react';

/**
 * CreateTaskModal component - Modal for creating a new task
 */
const CreateTaskModal = ({ isOpen, onClose, onCreateTask, assessmentQuestions }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    question_id: '',
    priority: 'medium',
    status: 'not_started',
    due_date: '',
    start_date: ''
  });

  // Define the exact order of categories to match TaskFilters
  const CATEGORY_ORDER = {
    'business': [
      'financial_performance',
      'revenue_quality',
      'customer_concentration',
      'management_team',
      'competitive_position',
      'growth_potential',
      'intellectual_property',
      'legal_compliance',
      'owner_dependency',
      'strategic_positioning'
    ],
    'family enterprise': [
      'family_governing',
      'succession_planning',
      'family_conflict',
      'equity_planning',
      'employment_policies',
      'family_council',
      'wealth_transfer',
      'values_mission',
      'education_development',
      'boundary_management'
    ],
    'personal financial': [
      'personal_financial',
      'wealth_management',
      'estate_planning',
      'tax_planning',
      'insurance_risk',
      'advisor_relationships',
      'legacy_planning',
      'liquidity_cash',
      'family_communication',
      'lifestyle_resource'
    ],
    'personal wellbeing': [
      'physical_health',
      'relational_health',
      'family_relationships',
      'emotional_mental',
      'purpose_identity',
      'time_management',
      'hobbies_interests',
      'social_connection',
      'exit_vision',
      'life_transition'
    ],
    'personal spiritual': [
      'personal_spiritual',
      'biblical_wisdom',
      'marriage_family',
      'business_stewardship',
      'character_integrity',
      'eternal_impact'
    ]
  };

  // Generate categories grouped by assessment type in the correct order
  const categoriesByType = useMemo(() => {
    if (!assessmentQuestions || assessmentQuestions.length === 0) return {};

    const groups = {
      'business': { label: 'Business Assessment', categories: [] },
      'family enterprise': { label: 'Family Enterprise', categories: [] },
      'personal financial': { label: 'Personal Financial', categories: [] },
      'personal wellbeing': { label: 'Personal Wellbeing', categories: [] },
      'personal spiritual': { label: 'Personal Spiritual', categories: [] }
    };

    const categoryMap = new Map();
    assessmentQuestions.forEach(q => {
      if (q.category && !categoryMap.has(q.category)) {
        const categoryData = {
          value: q.category,
          label: q.category_display || q.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          assessmentType: (q.assessment_type || 'business').toLowerCase()
        };
        categoryMap.set(q.category, categoryData);
      }
    });

    // Organize categories by assessment type in the specific order
    Object.entries(CATEGORY_ORDER).forEach(([type, orderedCategories]) => {
      if (groups[type]) {
        orderedCategories.forEach(catValue => {
          const cat = categoryMap.get(catValue);
          if (cat) {
            groups[type].categories.push(cat);
          }
        });
      }
    });

    return groups;
  }, [assessmentQuestions]);

  // Memoize filtered questions for Add Task modal to improve performance
  const filteredQuestionsForModal = useMemo(() => {
    if (!selectedCategory) return [];
    return assessmentQuestions.filter(q => q.category === selectedCategory);
  }, [selectedCategory, assessmentQuestions]);

  // Get the selected question's full text
  const selectedQuestion = useMemo(() => {
    if (!newTask.question_id) return null;
    return assessmentQuestions.find(q => q.question_id === newTask.question_id);
  }, [newTask.question_id, assessmentQuestions]);

  // Optimized handlers for form inputs to prevent lag
  const handleNewTaskChange = useCallback((field, value) => {
    setNewTask(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = () => {
    setSelectedCategory('');
    setShowSuccess(false);
    setNewTask({
      title: '',
      description: '',
      question_id: '',
      priority: 'medium',
      status: 'not_started',
      due_date: '',
      start_date: ''
    });
    onClose();
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!newTask.title || !newTask.question_id) {
      alert('Please fill in all required fields (Title and Question)');
      return;
    }

    try {
      const result = await onCreateTask(newTask);

      // Check if the task was created successfully
      if (result && result.success) {
        // Show success animation
        setShowSuccess(true);

        // Close modal after animation (increased to 1.5 seconds for better visibility)
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        // If task creation failed, show error
        alert(result?.error || 'Failed to create task. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert('An error occurred while creating the task.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-xl sticky top-0 z-20">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <h2 className="text-2xl font-bold mb-2">Add New Task</h2>
              <p className="text-sm text-blue-100">Create a new task linked to an assessment question</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => handleNewTaskChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-base focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter task title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newTask.description}
                onChange={(e) => handleNewTaskChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-base focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                rows="3"
                placeholder="Enter task description"
              />
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Question Category <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  const category = e.target.value;
                  setSelectedCategory(category);
                  handleNewTaskChange('question_id', ''); // Reset question when category changes
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select a category...</option>
                {Object.entries(categoriesByType).map(([typeKey, typeData]) => (
                  typeData.categories.length > 0 && (
                    <optgroup key={typeKey} label={typeData.label}>
                      {typeData.categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </optgroup>
                  )
                ))}
              </select>
            </div>

            {/* Question Selection - Only shown when category is selected */}
            {selectedCategory && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Assessment Question <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newTask.question_id}
                    onChange={(e) => handleNewTaskChange('question_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select a question...</option>
                    {filteredQuestionsForModal.map((q) => (
                      <option key={q.question_id} value={q.question_id}>
                        {q.question_id}: {q.subject}
                      </option>
                    ))}
                  </select>
                  {filteredQuestionsForModal.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No questions found for this category</p>
                  )}
                </div>

                {/* Full Question Text Display */}
                {selectedQuestion && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="block text-xs font-semibold text-blue-900 mb-2">
                      Full Question:
                    </label>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      {selectedQuestion.question_text}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Priority and Status in a grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={newTask.priority}
                  onChange={(e) => handleNewTaskChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={newTask.status}
                  onChange={(e) => handleNewTaskChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="under_review">Under Review</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={newTask.start_date}
                  onChange={(e) => handleNewTaskChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 mb-1.5"
                />
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const date = new Date();
                      handleNewTaskChange('start_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-green-100 hover:bg-green-200 text-green-700 rounded transition font-medium"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const date = new Date();
                      date.setDate(date.getDate() + 7);
                      handleNewTaskChange('start_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    7 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const date = new Date();
                      const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
                      handleNewTaskChange('start_date', nextMonth.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    Next Month
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const date = new Date();
                      const currentMonth = date.getMonth();
                      const currentQuarter = Math.floor(currentMonth / 3);
                      const nextQuarter = currentQuarter + 1;
                      const nextQuarterMonth = nextQuarter * 3;
                      const nextQuarterDate = new Date(date.getFullYear(), nextQuarterMonth, 1);
                      if (nextQuarterDate < date) {
                        nextQuarterDate.setFullYear(nextQuarterDate.getFullYear() + 1);
                      }
                      handleNewTaskChange('start_date', nextQuarterDate.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    Next Qtr
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNewTaskChange('start_date', '')}
                    className="px-1.5 py-0.5 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => handleNewTaskChange('due_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 mb-1.5"
                />
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const baseDate = newTask.start_date ? new Date(newTask.start_date) : new Date();
                      const date = new Date(baseDate);
                      date.setDate(date.getDate() + 7);
                      handleNewTaskChange('due_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    7d
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const baseDate = newTask.start_date ? new Date(newTask.start_date) : new Date();
                      const date = new Date(baseDate);
                      date.setMonth(date.getMonth() + 1);
                      handleNewTaskChange('due_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    1m
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const baseDate = newTask.start_date ? new Date(newTask.start_date) : new Date();
                      const date = new Date(baseDate);
                      date.setMonth(date.getMonth() + 3);
                      handleNewTaskChange('due_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    3m
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const baseDate = newTask.start_date ? new Date(newTask.start_date) : new Date();
                      const date = new Date(baseDate);
                      date.setMonth(date.getMonth() + 6);
                      handleNewTaskChange('due_date', date.toISOString().split('T')[0]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition font-medium"
                  >
                    6m
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNewTaskChange('due_date', '')}
                    className="px-1.5 py-0.5 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 rounded-b-xl flex justify-end items-center gap-3 border-t border-gray-200 sticky bottom-0 z-20">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium min-h-[44px]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium min-h-[44px]"
          >
            Create Task
          </button>
        </div>

        {/* Success Animation Overlay */}
        {showSuccess && (
          <div className="absolute inset-0 bg-white bg-opacity-95 rounded-xl flex items-center justify-center z-50 animate-fade-in">
            <div className="text-center animate-scale-in">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 animate-bounce">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Task Created!</h3>
              <p className="text-gray-600">Your task has been successfully added</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CreateTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateTask: PropTypes.func.isRequired,
  assessmentQuestions: PropTypes.arrayOf(PropTypes.shape({
    question_id: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    category_display: PropTypes.string,
    subject: PropTypes.string.isRequired,
    question_text: PropTypes.string,
    assessment_type: PropTypes.string,
  })).isRequired,
};

export default CreateTaskModal;
